import { isNullOrUndefined } from "utilities/object/object";

import { BTIconInfoCircleFilled } from "commonComponents/btWrappers/BTIcon";
import { BTLoading } from "commonComponents/utilities/BTLoading/BTLoading";
import { StatusTagDisplay } from "commonComponents/utilities/Status/StatusTagDisplay";

import { type DetailedVendorProductInformation } from "entity/vendor/common/vendor.types";
import VendorProductLink from "entity/vendor/VendorProductLink/VendorProductLink";

import "./VendorProductPopover.less";

export interface IVendorProductPopoverProps {
    /**
     * Vendor inventory
     */
    vendorInventory: DetailedVendorProductInformation | undefined;
    /**
     * Data test id
     */
    "data-testid"?: string;
}

const getTestId = (dataTestId: string | undefined, append: string) => {
    return dataTestId && dataTestId + "-" + append;
};

const VendorProductPopover: React.FunctionComponent<IVendorProductPopoverProps> = ({
    vendorInventory,
    "data-testid": dataTestId,
}) => {
    if (!vendorInventory) {
        return (
            <div
                className="VendorProductPopover Loading"
                data-testid={getTestId(dataTestId, "loading")}
            >
                <BTLoading displayMode="absolute" />
            </div>
        );
    }

    let stockLabel = (
        <div className="StockInfo">
            <StatusTagDisplay statusText={vendorInventory.count} statusType="info" />
            <span className="StockMessage">in stock at {vendorInventory.location}</span>
        </div>
    );

    if (vendorInventory.isDiscontinued) {
        stockLabel = <StatusTagDisplay statusText="Discontinued Product" statusType="danger" />;
    } else if (isNullOrUndefined(vendorInventory.count)) {
        stockLabel = <div className="StockInfo">Stock information currently unavailable</div>;
    } else if (vendorInventory.count === 0) {
        stockLabel = (
            <div className="StockInfo">
                <BTIconInfoCircleFilled type="warning" />
                <span className="StockMessage">Out of stock at {vendorInventory.location}</span>
            </div>
        );
    }

    // Allowing the clicks to bubble back up to the LIC causes line items to enter edit mode.
    const handleClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        e.stopPropagation();
    };

    // Allowing the focus events to bubble back up to the LIC causes line items to enter edit mode.
    const handleFocus = (e: React.FocusEvent<HTMLDivElement>) => {
        e.stopPropagation();
    };

    return (
        <div
            className="VendorProductPopover Container"
            data-testid={dataTestId}
            onClick={handleClick}
            onFocus={handleFocus}
        >
            <div>
                <img
                    className="ProductImage"
                    data-testid={getTestId(dataTestId, "image")}
                    src={vendorInventory.externalImageUrl}
                    alt="Product"
                    onError={(ev) => (ev.currentTarget.style.display = "none")}
                />
            </div>
            <div className="ProductInfo">
                <div className="margin-bottom-sm" data-testid={getTestId(dataTestId, "stock")}>
                    {stockLabel}
                </div>
                <div className="ProductName margin-bottom-sm">
                    <strong>{vendorInventory.productName}</strong>
                </div>
                <VendorProductLink
                    vendorType={vendorInventory.vendorType}
                    productLink={vendorInventory.externalProductLink}
                />
            </div>
        </div>
    );
};

export default VendorProductPopover;
